import { useRequestState } from "@/hooks";
import { useCallback, useContext, useEffect } from "react";
import { getUsers } from "@/services/api/user/requests";
import { selectUserStore, useBoundStore } from "@/services/stores";
import { AuthContext } from "@/guards/auth/auth-context";
import { StatusCodes } from "http-status-codes";
import {
  LoggedInUserNotFlatResponse,
  LoggedInUserResponse,
} from "@/services/api/user/contracts";
import { isUserFirstVisit } from "@/guards/user-configuration";

export const useUserData = (
  isFlatResponse: boolean,
  extraException = false,
) => {
  const { account } = useContext(AuthContext);
  const { setUser } = useBoundStore(selectUserStore);

  const { isLoading, data, error, setSuccess, setError } = useRequestState<
    LoggedInUserResponse | LoggedInUserNotFlatResponse
  >({
    isLoading: true,
  });

  const handleGetUsers = useCallback(() => {
    const controller = new AbortController();

    return getUsers(
      {
        email: account?.username ?? "",
      },
      { signal: controller.signal },
      isFlatResponse,
    )
      .then(response => {
        if (response) {
          if (response.Code !== StatusCodes.OK) {
            return Promise.reject({
              status: response.Code,
              statusText: response.Message,
            });
          }

          setSuccess(response.Data);
          setUser(response.Data);

          if (extraException) {
            isUserFirstVisit(response.Data);
          }
        } else {
          setError(response);

          return Promise.reject(response);
        }
      })
      .catch(error => {
        setError(error);
      });
  }, [
    account?.username,
    extraException,
    isFlatResponse,
    setError,
    setSuccess,
    setUser,
  ]);

  useEffect(() => {
    if (!data) {
      handleGetUsers();
    }
  }, [data, handleGetUsers]);

  return {
    isLoading,
    error,
    data,
    onGetUser: handleGetUsers,
  };
};
